"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  type countryApiType,
  type newCountryType,
  type optionType,
  type Translationtype,
} from "utils";
import { toastMessage } from "../components";
import { fetchCountries } from "../services/pricesService";

type UseFindCountryResult = {
  isLoading: boolean;
  error: boolean;
  allCountries: optionType[];
  shippingCountries: optionType[];
  languages: optionType[];
  currencies: optionType[];
  findByCode: (code: string) => countryApiType | undefined;
};

export function useFindCountry(t: Translationtype): UseFindCountryResult {
  const [countries, setCountries] = useState<countryApiType[]>([]);
  const [error, setError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchCountries()
      .then((resp) => {
        setCountries(resp);
        setError(false);
      })
      .catch(() => {
        toastMessage.error(t("Toast.errorGetCountry"));
        setError(true);
      })
      .finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const allCountries = useMemo(() => {
    if (!countries.length) return [];
    return countries
      .map((item) => ({
        label: item.friendlyName,
        value: item.code,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [countries]);

  const shippingCountries = useMemo(() => {
    return countries.reduce((acc: optionType[], country: newCountryType) => {
      if (country.shippingEnabled) {
        acc.push({ value: country.code, label: country.friendlyName });
      }
      return acc;
    }, []);
  }, [countries]);

  const languages = useMemo(() => {
    const set = new Set<string>();
    countries.forEach((c) => {
      if (c.shippingEnabled) {
        set.add(c.languageCode);
      }
    });
    return Array.from(set).map((lang) => ({
      label: lang,
      value: lang,
    }));
  }, [countries]);

  const currencies = useMemo(() => {
    const set = new Set<string>();
    countries.forEach((c) => {
      if (c.shippingEnabled) {
        set.add(c.defaultCurrencyCode);
      }
    });
    return Array.from(set).map((cur) => ({ label: cur, value: cur }));
  }, [countries]);

  const findByCode = useCallback(
    (code: string): countryApiType | undefined => {
      const found = countries.find((c) => c.code === code);
      if (!found) {
        console.error("❌ Country not found!", code);
      }
      return found;
    },
    [countries]
  );

  return {
    isLoading,
    error,
    allCountries,
    shippingCountries,
    languages,
    currencies,
    findByCode,
  };
}
